import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { ASYNC_DELETE_COMPANY, ASYNC_RESTORE_COMPANY } from 'store';
import { Link } from 'react-router-dom';
import { api } from 'services';
import { Row, Col, Card, Button } from 'react-bootstrap';
import Dialog from 'react-bootstrap-dialog';
import { useTranslation } from 'react-i18next';
import MasterPage from 'components/MasterPage';
import Listing from 'components/Listing';
import Can from 'components/Can';
import { DashboardOutlined } from '@material-ui/icons';

const CompaniesList = ({ deleteCompany, trash, restoreCompany }) => {
  const defaultFilters = { search: '', per_page: 10, page: 1 };
  const [data, setData] = useState({ data: [] });

  const [filters, setFilters] = useState(defaultFilters);

  let dialog;

  useEffect(() => {
    api
      .get(!trash ? 'companies' : 'companies/trash')
      .then(({ data }) => setData(data));
  }, [trash]);

  const { t } = useTranslation();

  useEffect(() => {
    api
      .get(!trash ? 'companies' : 'companies/trash', { params: { ...filters } })
      .then(({ data }) => setData(data));
  }, [filters, trash]);

  const effectDeleteCompany = id => {
    Dialog.setOptions({
      defaultOkLabel: t('label:yes'),
      defaultCancelLabel: t('label:no'),
      primaryClassName: 'btn-success',
      defaultButtonClassName: 'btn-danger'
    });

    dialog.show({
      title: t('title:company delete'),
      body: t('phrase:are you sure?'),
      actions: [
        Dialog.CancelAction(),
        Dialog.OKAction(() => deleteCompany(id))
      ],
      bgSize: 'small'
    });
  };

  const effectRestoreCompany = id => {
    Dialog.setOptions({
      defaultOkLabel: t('label:yes'),
      defaultCancelLabel: t('label:no'),
      primaryClassName: 'btn-success',
      defaultButtonClassName: 'btn-danger'
    });

    dialog.show({
      title: t('title:company restore'),
      body: t('phrase:are you sure?'),
      actions: [
        Dialog.CancelAction(),
        Dialog.OKAction(() => restoreCompany(id))
      ],
      bgSize: 'small'
    });
  };

  const handleFilterChange = (new_filters, reset = false) => {
    let f = { ...filters, ...new_filters };

    if (reset) f = { ...defaultFilters, ...new_filters };

    setFilters(f);
  };

  return (
    <>
      <Card.Body className="shadow bg-white rounded mb-3">
        <Dialog
          ref={component => {
            dialog = component;
          }}
        />
        <Row>
          <Col className="d-flex mt-3 mb-3">
            <DashboardOutlined className="icon-title" />
            <h4 className="title-dashboard mt-2">
              {!!trash ? t('title:trash') + ': ' : ''}
              {t('title:companies list')}
            </h4>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col>
            <Row className="mb-5">
              <Col>
                {!trash ? (
                  <>
                    <Can do="createCompany" on="App">
                      <Button
                        size="sm"
                        className="login-btn mr-1"
                        variant="primary-login"
                        as={Link}
                        to="/companies/create"
                      >
                        <i className="fas fa-plus"></i> {t('label:add new')}
                      </Button>
                    </Can>
                    &nbsp;
                    <Button
                      size="sm"
                      variant="secondary"
                      as={Link}
                      to="/companies/trash"
                    >
                      <i className="fas fa-trash"></i> {t('label:view trash')}
                    </Button>
                  </>
                ) : (
                    <Button size="sm" className="login-btn mr-1" variant="primary-login" as={Link} to="/companies">
                      <i className="fas fa-arrow-left"></i> {t('label:go back')}
                    </Button>
                  )}
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <Listing
                  fields={[
                    { name: 'company name', key: 'name', type: 'string' },
                    { name: 'fantasy name', key: 'alias', type: 'string' },
                    {
                      name: 'registered number',
                      key: 'document',
                      type: 'string'
                    },
                    {
                      name: 'period',
                      type: 'custom',
                      value: (_, data) => {
                        switch (data.quantity_rules) {
                          case 1:
                            return t('label:annual');
                          case 2:
                            return t('label:semiannual');
                          case 4:
                            return t('label:quarterly');
                          default:
                            return '--';
                        }
                      }
                    },
                    { name: 'period start', key: 'period_start', type: 'date' }
                  ]}
                  filters={[]}
                  stateFilters={filters}
                  setFilters={setFilters}
                  data={data}
                  handleFilterChange={handleFilterChange}
                  actions={
                    !trash
                      ? [
                        (company, k) => (
                          <Can do="editCompany" on="App">
                            <Button
                              size="sm"
                              key={k}
                              as={Link}
                              to={`/companies/edit/${company.id}`}
                              variant="light"
                              alt={t('title:edit')}
                              title={t('title:edit')}
                            >
                              <i className="fas fa-pen"></i>
                            </Button>
                          </Can>
                        ),
                        (company, k) => (
                          <Can do="deleteCompany" on="App">
                            <Button
                              size="sm"
                              key={k}
                              className="ml-1"
                              variant="danger"
                              onClick={() => effectDeleteCompany(company.id)}
                              alt={t('title:delete')}
                              title={t('title:delete')}
                            >
                              <i className="fas fa-times"></i>
                            </Button>
                          </Can>
                        )
                      ]
                      : [
                        (company, k) => (
                          <Can do="deleteCompany" on="App">
                            <Button
                              size="sm"
                              key={k}
                              variant="secondary"
                              onClick={() => effectRestoreCompany(company.id)}
                              alt={t('title:restore')}
                              title={t('title:restore')}
                            >
                              <i className="fas fa-trash-restore-alt"></i>
                            </Button>
                          </Can>
                        )
                      ]
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card.Body>
    </>
  );
};

const mapStateToProps = state => ({});

const mapDispatchToProps = dispatch => ({
  deleteCompany: id =>
    dispatch({ type: ASYNC_DELETE_COMPANY, payload: { id } }),
  restoreCompany: id =>
    dispatch({ type: ASYNC_RESTORE_COMPANY, payload: { id } })
});

const reduxConnect = connect(
  mapStateToProps,
  mapDispatchToProps
)(CompaniesList);

export default MasterPage(reduxConnect);
